/**
 * # RowWriter — the one write path of a row facade
 *
 * Every write a row facade makes (`SheetRow.Handle`: `update`,
 * `updateDurable`, `mergeAttributes`, `setLookupKey`, and every cell verb)
 * goes through ONE writer, whose FIRST step is the ONE promote precondition
 * (`Port.promoted`: an ephemeral sheet's COW promote awaited — it re-keys the
 * row — and the row's current wire key answered). A write is never sent
 * under a key read before that step.
 *
 * A row-field write (`update`) is one write of the client's row-field ledger
 * (`RowWrites`): entered (shown in every copy of the row and announced
 * before the first await) → the precondition → the send, its body the
 * row's projection of the fields it writes WHEN it is sent → accepted (the
 * follow-through: a data-key refetch, the deferred announcements) or
 * refused (only its own contribution taken back, as the SDK's correction;
 * the checked form recovers a PATCH that may have landed). A rename's
 * formula rewrites ride the label's verdict (`Port.renamed`). The writer
 * emits nothing itself: the ledger's displays and the stores announce.
 *
 * A cell write (`cell`) runs its transport after the precondition, with the
 * row's current key. What the transport is — the sheet's cell writer, the
 * rows-side source row's store, a standalone row's own request — is the
 * facade's.
 */
import type { Option } from "../core/option.ts";
import type { Actor } from "../tx/invalidation.ts";
import type { RowNetworkKey } from "../ids/row-network-key.ts";
import type { RowWrites } from "../ledger/row-writes.ts";

/** The one writer of a row facade (see the module doc). */
export class RowWriter {
  readonly #ledger: RowWrites;
  readonly #port: RowWriter.Port;

  constructor(ledger: RowWrites, port: RowWriter.Port) {
    this.#ledger = ledger;
    this.#port = port;
  }

  /**
   * Run a cell write: the precondition first, then `run` with the row's
   * current wire key. Rejects with the precondition's failure (nothing sent)
   * or `run`'s.
   */
  async cell<T>(run: (rowKey: RowNetworkKey) => Promise<T>): Promise<T> {
    const promoting = this.#port.promoted();
    // Nothing to wait for: sent in the turn it was issued (the row's writes
    // reach the wire in issue order).
    const rowKey = promoting == null ? this.#port.key() : await promoting;
    return await run(rowKey);
  }

  /**
   * Write `change` of the row's own fields as `actor` — `mode` `checked`
   * (awaits the verdict; a refusal or no answer rejects with
   * `RowWriteError("update")` after the recovery started) or `durable` (the
   * returned promise is the hand-off's `settled`). `dataKeyChanged`: the
   * change writes an attribute the row's data depends on — its
   * announcement waits for the acceptance (`Port.accepted`).
   */
  async update(change: RowWrites.Change, actor: Actor, mode: "checked" | "durable", dataKeyChanged: boolean): Promise<void> {
    const port = this.#port;
    const ledger = this.#ledger;
    const verdict = labelVerdict();
    // A rename rewrites the formulas naming the row by its old label:
    // painted before the label, sent once it landed, taken back with its
    // refusal.
    let rewrites: Option<Promise<void>> = null;
    let begun: Option<RowWrites.Write> = null;
    let prepared: {
      readonly write: RowWrites.Write;
      readonly rowKey: RowNetworkKey;
      readonly promoting: Option<Promise<RowNetworkKey>>;
    };
    try {
      rewrites = port.renamed(change, verdict.landed, mode, actor);
      // Observe immediately: a setup refusal may make the rollback reject
      // before this writer reaches its awaited follow-through.
      rewrites?.catch(() => {});
      const write = port.begin(change, actor, dataKeyChanged);
      begun = write;
      prepared = { write, rowKey: port.key(), promoting: port.promoted() };
    } catch (err) {
      // begin takes its own failed display back; a later synchronous
      // precondition failure must also take the entered label back.
      if (begun != null) {
        try {
          ledger.refuse(begun, err, "");
        } catch {
          // A correction observer cannot replace the initiating failure.
        }
      }
      verdict.refuse(err);
      if (rewrites != null) await rewrites.catch(() => {});
      throw err;
    }
    const { write, promoting } = prepared;
    let rowKey = prepared.rowKey;
    let answer: RowWrites.Answer;
    if (promoting != null) {
      try {
        // The one precondition: an ephemeral sheet is promoted first, and
        // the row addressed by the key the promote gave it (a lookup-seeded
        // row persisted there).
        rowKey = await promoting;
        await port.persisted(rowKey);
      } catch (err) {
        // The take-back is the SDK's correction, never the writer's echo.
        try {
          ledger.refuse(write, err, "");
        } catch {
          // Complete the rename rollback even if its correction observer fails.
        }
        verdict.refuse(err);
        if (rewrites != null) await rewrites.catch(() => {});
        if (mode === "checked") {
          try {
            port.recover(err, rowKey);
          } catch {
            // The precondition's failure remains the caller's failure.
          }
        }
        throw err;
      }
    }
    try {
      answer = await ledger.send(write, port.transport(mode, rowKey));
    } catch (err) {
      const error = ledger.refuse(write, err, "");
      verdict.refuse(error);
      // No verdict (or one the SDK could not read): the PATCH may have
      // landed — the checked form re-reads the row (a durable hand-off's
      // refusal is re-read by the client).
      if (mode !== "checked") throw err;
      port.recover(error, rowKey);
      throw error;
    }
    ledger.accept(write);
    verdict.accept();
    port.accepted(write, answer, actor, dataKeyChanged);
    if (rewrites != null) await rewrites;
  }
}

/** Types that go with `RowWriter`. */
export namespace RowWriter {
  /** What a writer asks of the row it writes (the facade). */
  export interface Port {
    /** The row's wire key as it is now (no promote awaited). */
    key(): RowNetworkKey;
    /**
     * The ONE precondition of every write: the sheet's COW promote to
     * await, answering the row's current wire key — `null` when nothing can
     * intervene (a standalone row): the write is sent in the turn it was
     * issued, under `key()`.
     */
    promoted(): Option<Promise<RowNetworkKey>>;
    /** A row that must exist before a PATCH reaches it (a lookup-seeded row) persisted, after the promote. */
    persisted(rowKey: RowNetworkKey): Promise<void>;
    /** Enter `change` in the row-field ledger, shown and announced as `actor` (`quiet`: attribute keys deferred). */
    begin(change: RowWrites.Change, actor: Actor, quiet: boolean): RowWrites.Write;
    /** How a body reaches the server: checked (`Backend.updateRow`) or durable (the debounce executor). */
    transport(mode: "checked" | "durable", rowKey: RowNetworkKey): RowWrites.Transport;
    /** A rename's formula rewrites, sent once `landed` resolves; `null` when `change` renames nothing. */
    renamed(change: RowWrites.Change, landed: Promise<void>, mode: "checked" | "durable", actor: Actor): Option<Promise<void>>;
    /** A checked write's failure that may have stored: recover (re-read the row). */
    recover(error: unknown, rowKey: RowNetworkKey): void;
    /** A landed write's follow-through (a data-key refetch, the deferred announcements). */
    accepted(write: RowWrites.Write, answer: RowWrites.Answer, actor: Actor, dataKeyChanged: boolean): void;
  }
}

/**
 * A label write's verdict for a rename's rewrites: `landed` settles when the
 * write does. Never an unhandled rejection: a rename that reaches no formula
 * leaves it unobserved.
 */
function labelVerdict(): { readonly landed: Promise<void>; accept(): void; refuse(err: unknown): void } {
  let accept: () => void = () => {};
  let refuse: (err: unknown) => void = () => {};
  const landed = new Promise<void>((resolve, reject) => {
    accept = resolve;
    refuse = reject;
  });
  landed.catch(() => {});
  return { landed, accept, refuse };
}
